'use client'

import type * as LabelPrimitive from '@radix-ui/react-label'

import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
  useFormState
} from 'react-hook-form'

import { cn } from '../../utils/cn'
import { Label } from './Label'

const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues extends FieldValues | undefined = undefined
>({
  ...props
}: ControllerProps<TFieldValues, TName, TTransformedValues>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState } = useFormContext()
  const formState = useFormState({ name: fieldContext.name })
  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>')
  }

  const { id, errorVisible } = itemContext

  // Single source of truth for surfacing validation state: `FormMessage` visibility and
  // `FormControl`'s `aria-invalid`/`data-valid` all derive from it, so a visible error always
  // implies an invalid accessible state on the control (and vice versa).
  const showValidationState =
    errorVisible ?? (fieldState.isTouched || fieldState.isDirty || formState.isSubmitted)

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    showValidationState,
    ...fieldState
  }
}

type FormItemContextValue = {
  id: string
  errorVisible?: boolean
}

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue)

interface FormItemProps extends React.ComponentProps<'div'> {
  /**
   * Overrides the default interaction gate (touched, dirty, or submitted) deciding when the
   * field's validation state surfaces — `FormMessage` visibility and `FormControl`'s
   * `aria-invalid`/`data-valid` alike. Useful for errors that arrive from external state changes
   * (data resolving after mount, values set programmatically) rather than from user input —
   * hiding those leaves no visible explanation for a blocked form.
   */
  errorVisible?: boolean
}

function FormItem({ className, errorVisible, ...props }: FormItemProps) {
  const id = React.useId()

  return (
    <FormItemContext.Provider value={{ id, errorVisible }}>
      <div data-slot="form-item" className={cn('grid gap-2', className)} {...props} />
    </FormItemContext.Provider>
  )
}

function FormLabel({ className, ...props }: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formItemId, showValidationState } = useFormField()

  // Mirror FormMessage's visibility gate: a pristine field (e.g. an unfilled default) must
  // not paint the label red when no message is shown alongside it.

  return (
    <Label
      data-slot="form-label"
      data-error={showValidationState && !!error}
      className={cn(
        'data-[error=true]:text-destructive-foreground',
        'text-2xs text-secondary-foreground',
        className
      )}
      htmlFor={formItemId}
      {...props}
    />
  )
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId, invalid, showValidationState } =
    useFormField()

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={showValidationState && invalid}
      data-valid={showValidationState && !invalid && !error}
      {...props}
    />
  )
}

function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField()

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

function FormMessage({ className, ...props }: React.ComponentProps<'p'>) {
  const { error, formMessageId, showValidationState } = useFormField()

  const errorMessage = error ? String(error?.message ?? '') : props.children
  if (!errorMessage) return null

  if (!showValidationState) return null

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn('text-destructive-foreground text-2xs', className)}
      {...props}
    >
      {errorMessage}
    </p>
  )
}

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField
}
